import { useEffect } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { X, Home, Gift, ChevronRight, Phone, Bike, Wrench, BatteryCharging, Shield } from 'lucide-react'
import { MAIN_NAV } from '../../config/nav.config'
import { useMobileMenuStore } from '../../state/mobileMenuStore'
import { useCategories } from '@/modules/categories-api'
import { useSettings } from '@/modules/settings-api'

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'bike': Bike,
  'wrench': Wrench,
  'battery-charging': BatteryCharging,
  'shield': Shield,
}

export function MobileMenuDrawer() {
  const open = useMobileMenuStore((s) => s.open)
  const close = useMobileMenuStore((s) => s.closeMenu)
  const { pathname } = useLocation()
  const { data: categories } = useCategories()
  const { data: settings } = useSettings()

  useEffect(() => { if (open) close() }, [pathname]) // eslint-disable-line

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [open, close])

  const sortedCats = (categories ?? []).slice().sort((a, b) => a.order - b.order)

  return (
    <>
      <div
        onClick={close}
        className={[
          'fixed inset-0 bg-black/40 z-40 transition-opacity lg:hidden',
          open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        ].join(' ')}
      />
      <aside
        className={[
          'fixed top-0 left-0 h-full w-[88%] max-w-[360px] bg-surface shadow-2xl z-50 flex flex-col transition-transform duration-300 lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
        aria-hidden={!open}
      >
        <header className="h-16 px-5 border-b border-border flex items-center justify-between shrink-0 bg-brand-600 text-white">
          <div className="font-semibold">{settings.shopName}</div>
          <button
            onClick={close}
            className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center transition"
            aria-label="Đóng"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto">
          <div className="p-4 border-b border-border">
            <div className="text-xs font-semibold text-muted uppercase mb-2 px-1">Điều hướng</div>
            <ul className="space-y-0.5">
              {MAIN_NAV.map((item, idx) => {
                const isHome = idx === 0
                const isPromo = item.label === 'Khuyến mãi'
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={isHome}
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-3 h-11 px-3 rounded-md text-[15px] font-medium transition',
                          isActive ? 'bg-brand-50 text-brand-700' : 'text-text hover:bg-slate-50',
                        ].join(' ')
                      }
                    >
                      {isHome && <Home className="w-4 h-4 text-brand-600" />}
                      {isPromo && <Gift className="w-4 h-4 text-brand-600" />}
                      <span className="flex-1">{item.label}</span>
                      <ChevronRight className="w-4 h-4 text-muted" />
                    </NavLink>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="p-4 border-b border-border">
            <div className="text-xs font-semibold text-muted uppercase mb-2 px-1">Danh mục sản phẩm</div>
            <ul className="space-y-0.5">
              {sortedCats.map((cat) => {
                const Icon = ICONS[cat.icon] ?? Bike
                return (
                  <li key={cat.id}>
                    <Link
                      to={`/danh-muc/${cat.slug}`}
                      className="flex items-center gap-3 h-11 px-3 rounded-md text-[15px] text-text hover:bg-brand-50 hover:text-brand-600 transition"
                    >
                      <Icon className="w-4 h-4 text-brand-600" />
                      <span className="flex-1">{cat.label}</span>
                      <ChevronRight className="w-4 h-4 text-muted" />
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>

          <div className="p-5 bg-brand-50">
            <div className="text-xs text-muted">Hotline tư vấn</div>
            <a href={`tel:${settings.hotline.replace(/\s/g, '')}`} className="mt-1 flex items-center gap-2 text-brand-700 font-bold text-xl">
              <Phone className="w-5 h-5" /> {settings.hotline}
            </a>
          </div>
        </div>
      </aside>
    </>
  )
}
